import React, { useReducer, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Shield, Lock, Building2, Loader2, LogIn } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const initial = { inviteId: '', pin: '', loading: false };
function reducer(state, action) {
  switch (action.type) {
    case 'SET_INVITE': return { ...state, inviteId: action.payload };
    case 'SET_PIN':    return { ...state, pin: action.payload };
    case 'LOADING':    return { ...state, loading: true };
    case 'DONE':       return { ...state, loading: false };
    default: return state;
  }
}

/**
 * Página PÚBLICA de acesso do Auditor Externo (TCE / CGU / MP).
 * URL: /auditor?invite=<id>
 *
 * Fluxo:
 *  1. Usuário cola/usa invite_id e o PIN de 6 dígitos enviado pelo órgão
 *  2. Recebe JWT de 8h com role AUDITOR_EXTERNO
 *  3. Redirecionado para o dashboard (somente leitura)
 */
export default function AuditorExternoLoginPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [state, dispatch] = useReducer(reducer, initial);

  // Auto-popula invite_id se vier por URL
  useEffect(() => {
    const inv = searchParams.get('invite');
    if (inv) dispatch({ type: 'SET_INVITE', payload: inv });
  }, [searchParams]);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!state.inviteId || !state.pin) {
      toast.error('Informe o ID do convite e o PIN');
      return;
    }
    dispatch({ type: 'LOADING' });
    try {
      const res = await axios.post(`${BACKEND}/api/auditor-externo/access`, {
        invite_id: state.inviteId.trim(),
        pin: state.pin.trim(),
      });
      // Persistir token igual ao AuthContext espera
      localStorage.setItem('token', res.data.token);
      localStorage.setItem('user', JSON.stringify(res.data.user));
      toast.success(`Bem-vindo(a), ${res.data.user.name}`);
      // Redireciona para visão somente-leitura e força reload do contexto
      window.location.href = '/dashboard';
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Falha ao validar acesso');
      dispatch({ type: 'DONE' });
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-950 to-blue-900 flex items-center justify-center p-4" data-testid="auditor-login-page">
      <div className="max-w-md w-full">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-white/10 backdrop-blur-md border border-white/20 mb-4">
            <Shield className="w-8 h-8 text-blue-300" />
          </div>
          <h1 className="text-3xl md:text-4xl font-extrabold text-white tracking-tight">Acesso Auditor Externo</h1>
          <p className="text-blue-200 mt-2 text-sm">Para TCE · CGU · MP · Auditorias externas</p>
        </div>

        <form onSubmit={onSubmit} className="bg-white rounded-2xl shadow-2xl p-6 md:p-8 space-y-4" data-testid="auditor-form">
          <div>
            <label className="text-xs uppercase tracking-wider font-bold text-slate-500 mb-1.5 flex items-center gap-1.5">
              <Building2 className="w-3.5 h-3.5" /> ID do Convite
            </label>
            <input
              type="text"
              value={state.inviteId}
              onChange={(e) => dispatch({ type: 'SET_INVITE', payload: e.target.value })}
              placeholder="audext_xxxxxxxxxxxx"
              className="w-full rounded-lg border border-slate-300 px-3 py-2.5 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-blue-500"
              data-testid="auditor-invite-input"
              autoComplete="off"
            />
          </div>

          <div>
            <label className="text-xs uppercase tracking-wider font-bold text-slate-500 mb-1.5 flex items-center gap-1.5">
              <Lock className="w-3.5 h-3.5" /> PIN de Acesso (6 dígitos)
            </label>
            <input
              type="text"
              inputMode="numeric"
              maxLength={6}
              value={state.pin}
              onChange={(e) => dispatch({ type: 'SET_PIN', payload: e.target.value.replace(/\D/g, '') })}
              placeholder="••••••"
              className="w-full rounded-lg border border-slate-300 px-3 py-2.5 text-2xl tracking-[0.4em] text-center font-bold focus:outline-none focus:ring-2 focus:ring-blue-500"
              data-testid="auditor-pin-input"
              autoComplete="off"
            />
          </div>

          <button
            type="submit"
            disabled={state.loading}
            className="w-full bg-gradient-to-r from-blue-700 to-blue-800 hover:from-blue-800 hover:to-blue-900 disabled:opacity-60 text-white font-bold py-3 rounded-lg flex items-center justify-center gap-2 transition-all"
            data-testid="auditor-submit-btn"
          >
            {state.loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <LogIn className="w-5 h-5" />}
            {state.loading ? 'Validando…' : 'Acessar como Auditor'}
          </button>

          <div className="text-[11px] text-slate-500 leading-relaxed pt-2 border-t border-slate-100">
            <strong className="text-slate-700">Aviso:</strong> Sua sessão é registrada em trilha de auditoria.
            Acesso somente-leitura. Sessão válida por 8 horas. Dados protegidos pela LGPD.
          </div>
        </form>

        <p className="text-center text-blue-200/60 text-xs mt-6">
          BlueGov · Plataforma de Conformidade e Proteção Institucional
        </p>
      </div>
    </div>
  );
}
